import { configCategory, type ConfigCategoryLayoutElement } from "@pub/config";
import { NoSSR } from "@sphtech/web2-core/components";
import { Fragment, ReactNode } from "react";

import CategoryStoryListing from "./CategoryStoryListing";
import CategoryAnchorBlock from "./Elements/CategoryAnchorBlock";
import CategoryAnchorCarousel from "./Elements/CategoryAnchorCarousel";
import CategoryIntroBlock from "./Elements/CategoryIntroBlock";
import CategorySocialVideoPortraitBlock from "./Elements/CategorySocialVideoPortraitBlock";
import CategoryStoryListingBlock from "./Elements/CategoryStoryListingBlock";
import CategorySubAnchorBlock from "./Elements/CategorySubAnchorBlock";
import CategorySubAnchorBlockWithVideo from "./Elements/CategorySubAnchorBlockWithVideo";
import CategoryVideoGalleryBlock from "./Elements/CategoryVideoGalleryBlock";
import {
  CategoryLayoutData,
  type CategoryLayoutElementsMap,
} from "./types/types";

type Props = {
  data: CategoryLayoutData;
};

// Default layout elements order for category page.
// This can be removed once all publications have defined their own layout in the config.
const defaultLayoutElements: ConfigCategoryLayoutElement[] = [
  { name: "introBlock" },
  { name: "anchorBlock" },
  { name: "subAnchorBlockWithVideo" },
  { name: "storyListingBlock" },
];

const categoryLayoutElements: CategoryLayoutElementsMap = {
  introBlock: CategoryIntroBlock,
  anchorBlock: CategoryAnchorBlock,
  subAnchorBlock: CategorySubAnchorBlock,
  subAnchorBlockWithVideo: CategorySubAnchorBlockWithVideo,
  videoGalleryBlock: CategoryVideoGalleryBlock,
  socialVideoPortraitBlock: CategorySocialVideoPortraitBlock,
  anchorCarousel: CategoryAnchorCarousel,
  storyListingBlock: CategoryStoryListingBlock,
};

function CategoryLayout({ data }: Props): ReactNode {
  const ignoreIds: string[] = [];

  if (data.anchorBlock?.id) {
    ignoreIds.push(data.anchorBlock.id);
  }

  if (data.latestArticle) {
    data.latestArticle.forEach((item) => {
      if (item.id) {
        ignoreIds.push(item.id);
      }
    });
  }

  if (data.mainContentBlock) {
    data.mainContentBlock.forEach((item) => {
      if (item.id) {
        ignoreIds.push(item.id);
      }
    });
  }

  const layoutElements =
    (data.path && configCategory.layoutGridByPath?.[data.path]) ||
    configCategory.layoutGrid ||
    defaultLayoutElements;

  return (
    <Fragment>
      {layoutElements.map((element, index) => {
        const ElementComponent = categoryLayoutElements[element.name];

        return (
          <Fragment key={index}>
            <ElementComponent data={data} />
          </Fragment>
        );
      })}

      <NoSSR>
        <CategoryStoryListing
          data={{
            uniqSectionName: data.uniqSectionName,
            tagSlug: data.tagSlug,
            limit: 8,
            ignoreIds,
            lb1: data.lb1,
            imu: data.imu2,
            userSubmissionBox: data.userSubmissionBox,
            queryLegacyTagData: data.queryLegacyTagData,
            storyType: data.storyType,
          }}
        />
      </NoSSR>
    </Fragment>
  );
}

export default CategoryLayout;
